import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Popover } from '@mui/material'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { colors } from '../../const/colors'
import { CALENDAR_SX } from '../Table/DateFilterPill'
import { dateIso2localeString } from '../../utils/formatDate'

const MAX_ADVANCE_MONTHS = 1

const IcoCalendarSmall = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M8 3v4M16 3v4M3 11h18" />
  </svg>
)

interface DayPickerPillProps {
  value: string
  onChange: (day: string) => void
}

const DayPickerPill = ({ value, onChange }: DayPickerPillProps) => {
  const { t, i18n } = useTranslation(['parking_pool_data'])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleSelect = (newValue: Dayjs | null) => {
    if (newValue) onChange(newValue.format('YYYY-MM-DD'))
    setAnchorEl(null)
  }

  const isToday = value === dayjs().format('YYYY-MM-DD')
  const label = dateIso2localeString(value, i18n.language, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  return (
    <>
      <Box
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          p: '8px 16px',
          borderRadius: '20px',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff',
          fontSize: '13px',
          fontWeight: 700,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          transition: 'background .15s',
          '&:hover': { background: 'rgba(255,255,255,0.14)' },
        }}
      >
        <IcoCalendarSmall />
        {label}
        {isToday && (
          <Box
            component="span"
            sx={{ color: colors.greenMint, fontWeight: 700 }}
          >
            {t('day_today')}
          </Box>
        )}
      </Box>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            mt: '6px',
            borderRadius: '18px',
            boxShadow: '0 16px 40px -12px rgba(19,54,73,.28)',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
          },
        }}
      >
        <StaticDatePicker
          value={dayjs(value)}
          onChange={handleSelect}
          maxDate={dayjs().add(MAX_ADVANCE_MONTHS, 'month')}
          sx={CALENDAR_SX}
          slotProps={{
            toolbar: { hidden: true },
            actionBar: { actions: [] },
          }}
        />
      </Popover>
    </>
  )
}

export default DayPickerPill
